import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { createApiUrl } from '../../utils/apiConfig';
import styles from './AssignedQuizzesTab.module.css';

const AssignedQuizzesTab = ({ 
  tabId: _tabId, 
  tabLabel: _tabLabel, 
  assignedQuizzes, 
  loadingAssignedQuizzes, 
  onUpdateStatus, 
  onRefresh 
}) => {
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [quizResponses, setQuizResponses] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(1800); // Default to 30 minutes
  const [quizTimer, setQuizTimer] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    return () => {
      if (quizTimer) {
        clearInterval(quizTimer);
      }
    };
  }, [quizTimer]);

  const startQuiz = async (quiz) => {
    
    // Calculate duration first and set initial time
    const duration = quiz.estimatedDuration || 
                   quiz.settings?.estimatedDuration || 
                   quiz.settings?.timeLimit || 
                   30; // Default to 30 minutes if no duration found
    const fullDuration = duration * 60;
    
    setSelectedQuiz(quiz);
    setCurrentQuestionIndex(0);
    setQuizResponses({});
    setIsPaused(false);
    setTimeRemaining(fullDuration);
    
    // Update status to in-progress if not already started
    if (quiz.assignmentStatus === 'assigned') {
      onUpdateStatus(quiz._id, 'in-progress');
    }
    
    // Only try to load existing progress if quiz is already in-progress
    if (quiz.assignmentStatus === 'in-progress') {
      try {
        const progressResponse = await axios.get(
          createApiUrl(`/api/quiz/${quiz._id}/progress`),
          { withCredentials: true }
        );
        
        if (progressResponse.data.success && progressResponse.data.data) {
          const progressData = progressResponse.data.data;
          
          setQuizResponses(progressData.responses || {});
          setCurrentQuestionIndex(progressData.currentQuestionIndex || 0);
          setIsPaused(progressData.isPaused || false);
          
          // Use the saved time remaining (timer pauses when logged out)
          // Only use saved time if it's greater than 0 (not expired)
          if (progressData.timeRemaining !== undefined && progressData.timeRemaining !== null && progressData.timeRemaining > 0) {
            setTimeRemaining(progressData.timeRemaining);
          } else {
            // Continue
          }
        }
        // If no existing progress, keep the fullDuration we already set
      } catch (error) {
        // Log error
      }
    } else {
      // Continue
    }
    
    // Set timer if quiz has time limit
    if (duration && duration > 0) {
            const timer = setInterval(() => {
              setTimeRemaining(prev => {
                if (prev <= 1) {
                  clearInterval(timer);
                  handleSubmitQuiz();
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
            setQuizTimer(timer);
          } else {
            // Continue
          }
    
    // Save initial progress to database
    setTimeout(() => {
      saveProgress();
    }, 100); // Small delay to ensure state is updated
    
    setShowQuizModal(true);
  };

  const handleAnswerChange = (questionId, answer) => {
    setQuizResponses(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const saveProgress = async () => {
    if (!selectedQuiz) return;
    
    try {
      await axios.post(
        createApiUrl(`/api/quiz/${selectedQuiz._id}/save-progress`),
        {
          responses: quizResponses,
          currentQuestionIndex,
          timeRemaining,
          isPaused
        },
        { withCredentials: true }
      );
      
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const resetQuizProgress = async () => {
    if (!selectedQuiz) return;
    
    try {
      // Clear the quiz response from database
      await axios.delete(
        createApiUrl(`/api/quiz/${selectedQuiz._id}/progress`),
        { withCredentials: true }
      );
      
      // Clear existing timer
      if (quizTimer) {
        clearInterval(quizTimer);
        setQuizTimer(null);
      }
      
      // Reset local state
      setQuizResponses({});
      setCurrentQuestionIndex(0);
      setIsPaused(false);
      
      // Reset timer to full duration - check multiple possible locations
      const duration = selectedQuiz.estimatedDuration || 
                     selectedQuiz.settings?.estimatedDuration || 
                     selectedQuiz.settings?.timeLimit || 
                     30; // Default to 30 minutes if no duration found
      const fullDuration = duration * 60;
      setTimeRemaining(fullDuration);
      
      // Restart timer if quiz has time limit
      if (duration && duration > 0) {
        
        const timer = setInterval(() => {
          setTimeRemaining(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              handleSubmitQuiz();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        setQuizTimer(timer);
      }
      
      // Update quiz status back to assigned and reset attempt number
      onUpdateStatus(selectedQuiz._id, 'assigned', { attempts: 0 });
      
      // Refresh the quiz data from database
      if (onRefresh) {
        onRefresh();
      }
      
    } catch (error) {
      console.error('Error resetting quiz progress:', error);
    }
  };

  const handlePauseQuiz = async () => {
    if (isPaused) {
      // Resume quiz
      setIsPaused(false);
      if (quizTimer) {
        clearInterval(quizTimer);
      }
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleSubmitQuiz();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      setQuizTimer(timer);
    } else {
      // Pause quiz
      setIsPaused(true);
      if (quizTimer) {
        clearInterval(quizTimer);
        setQuizTimer(null);
      }
      // Save progress when pausing
      await saveProgress();
    }
  };

  const handleNextQuestion = async () => {
    if (currentQuestionIndex < selectedQuiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      // Save progress when navigating (but don't pause timer)
      await saveProgress();
    }
  };

  const handlePreviousQuestion = async () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      // Save progress when navigating (but don't pause timer)
      await saveProgress();
    }
  };

  const handleSubmitQuiz = async () => {
    try {

      if (quizTimer) {
        clearInterval(quizTimer);
        setQuizTimer(null);
      }

      const response = await axios.post(
        createApiUrl(`/api/quiz/${selectedQuiz._id}/submit`),
        {
          responses: quizResponses,
          timeSpent: selectedQuiz.estimatedDuration ? (selectedQuiz.estimatedDuration * 60) - timeRemaining : null
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        // Update quiz status locally
        onUpdateStatus(selectedQuiz._id, 'completed', response.data.data);
        setShowQuizModal(false);
        setSelectedQuiz(null);
        setQuizResponses({});
        setCurrentQuestionIndex(0);
        setTimeRemaining(0);
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
    }
  };

  const viewQuizResults = async (quiz) => {
    
    // Load the quiz results from the database
    try {
      const [progressResponse, assignmentResponse] = await Promise.all([
        axios.get(createApiUrl(`/api/quiz/${quiz._id}/progress`), { withCredentials: true }),
        axios.get(createApiUrl(`/api/quiz-assignments/student/${quiz.assignmentId || quiz._id}`), { withCredentials: true })
      ]);
      
      if (progressResponse.data.success && progressResponse.data.data) {
        const resultData = progressResponse.data.data;
        
        // Combine regular responses and essay responses
        const allResponses = { ...(resultData.responses || {}), ...(resultData.essayResponses || {}) };
        
        // Get the latest assignment data with essay grades
        let updatedQuiz = { ...quiz };
        if (assignmentResponse.data.success && assignmentResponse.data.assignments) {
          // Find the specific assignment for this quiz
          const assignmentData = assignmentResponse.data.assignments.find(assignment => 
            assignment.quizId._id === quiz._id || assignment._id === quiz.assignmentId
          );
          
          if (assignmentData) {
            updatedQuiz = {
              ...quiz,
              percentage: assignmentData.score || assignmentData.percentage,
              essayGrades: assignmentData.essayGrades,
              assignmentStatus: assignmentData.status
            };
          } else {
            // Continue
          }
        } else {
          // Continue
        }
        
        // Set up the quiz for results view
        setSelectedQuiz(updatedQuiz);
        setQuizResponses(allResponses);
        setCurrentQuestionIndex(0);
        setIsPaused(true); // Don't show timer for results
        setTimeRemaining(0); // No timer for results
        setShowQuizModal(true);
      } else {
        console.error('No quiz results found');
        // Fallback to starting the quiz if no results found
        startQuiz(quiz);
      }
    } catch (error) {
      console.error('Error loading quiz results:', error);
      // Fallback to starting the quiz if error loading results
      startQuiz(quiz);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'assigned': { className: 'assigned', text: 'Not Started' },
      'in-progress': { className: 'in-progress', text: 'In Progress' },
      'completed': { className: 'completed', text: 'Completed' },
      'graded': { className: 'graded', text: 'Graded' }
    };
    
    const config = statusConfig[status] || { className: 'assigned', text: 'Not Started' };
    return (
      <span className={`${styles.statusBadge} ${styles[config.className]}`}>
        {config.text}
      </span>
    );
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 90) return styles.scoreExcellent;
    if (percentage >= 80) return styles.scoreGood;
    if (percentage >= 70) return styles.scoreAverage;
    return styles.scorePoor;
  };

  if (loadingAssignedQuizzes) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading assigned quizzes...</p>
      </div>
    );
  }

  return (
    <div className={styles.assignedQuizzesTab}>
      <div className={styles.header}>
        <h3 className={styles.title}>Assigned Quizzes</h3>
        <button 
          className={styles.refreshButton}
          onClick={onRefresh}
          disabled={loadingAssignedQuizzes}
        >
          🔄 Refresh
        </button>
      </div>

      {assignedQuizzes.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>❓</div>
          <h4>No Quizzes Assigned</h4>
          <p>You don't have any quizzes assigned yet. Check back later or ask your teacher for assignments.</p>
        </div>
      ) : (
        <div className={styles.quizzesGrid}>
          {assignedQuizzes.map((quiz) => (
            <div key={quiz._id} className={styles.quizCard}>
              <div className={styles.quizHeader}>
                <h4 className={styles.quizTitle}>{quiz.title}</h4>
                {getStatusBadge(quiz.assignmentStatus)}
              </div>
              
              <div className={styles.quizInfo}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Questions:</span>
                  <span className={styles.infoValue}>{quiz.questions?.length || 0}</span>
                </div>
                
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Points:</span>
                  <span className={styles.infoValue}>{quiz.totalPoints || 0}</span>
                </div>
                
                {quiz.estimatedDuration && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Duration:</span>
                    <span className={styles.infoValue}>{quiz.estimatedDuration} min</span>
                  </div>
                )}
                
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Assigned:</span>
                  <span className={styles.infoValue}>
                    {new Date(quiz.assignedAt).toLocaleDateString()}
                  </span>
                </div>
                
                {quiz.completedAt && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Completed:</span>
                    <span className={styles.infoValue}>
                      {new Date(quiz.completedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
                
                {quiz.percentage !== undefined && quiz.percentage !== null && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Score:</span>
                    <span className={`${styles.infoValue} ${getScoreColor(quiz.percentage)}`}>
                      {quiz.percentage}%
                    </span>
                  </div>
                )}
              </div>
              
              {quiz.description && (
                <div className={styles.quizDescription}>
                  <p>{quiz.description}</p>
                </div>
              )}
              
              <div className={styles.quizActions}>
                {quiz.assignmentStatus === 'assigned' && (
                  <button 
                    className={styles.startButton}
                    onClick={() => startQuiz(quiz)}
                  >
                    Start Quiz
                  </button>
                )}
                
                {quiz.assignmentStatus === 'in-progress' && (
                  <button 
                    className={styles.continueButton}
                    onClick={() => startQuiz(quiz)}
                  >
                    Continue Quiz
                  </button>
                )}
                
                {quiz.assignmentStatus === 'completed' && (
                  <button 
                    className={styles.viewButton}
                    onClick={() => viewQuizResults(quiz)}
                  >
                    View Results
                  </button>
                )}
                
                {quiz.assignmentStatus === 'graded' && (
                  <button 
                    className={styles.viewButton}
                    onClick={() => viewQuizResults(quiz)}
                  >
                    View Grade
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quiz Taking Modal */}
      {showQuizModal && selectedQuiz && (
        <div className={styles.quizModal}>
          <div className={styles.quizModalContent}>
            <div className={styles.quizModalHeader}>
              <h3>{selectedQuiz.title}</h3>
              <div className={styles.quizModalInfo}>
                {(() => {
                  const _showTimer = timeRemaining > 0;
                  return null;
                })()}
                {selectedQuiz && (selectedQuiz.assignmentStatus !== 'completed' && selectedQuiz.assignmentStatus !== 'graded') && (
                  <div className={styles.timer}>
                    Time Remaining: {formatTime(timeRemaining)}
                  </div>
                )}
                {selectedQuiz && (selectedQuiz.assignmentStatus !== 'completed' && selectedQuiz.assignmentStatus !== 'graded') && (
                  <button 
                    className={styles.pauseButton}
                    onClick={handlePauseQuiz}
                  >
                    {isPaused ? '▶️ Resume' : '⏸️ Pause'}
                  </button>
                )}
                {(selectedQuiz.assignmentStatus !== 'completed' && selectedQuiz.assignmentStatus !== 'graded') && (
                  <button 
                    className={styles.resetButton}
                    onClick={resetQuizProgress}
                    title="Reset Quiz Progress"
                  >
                    🔄 Reset
                  </button>
                )}
                <button 
                  className={styles.closeButton}
                  onClick={async () => {
                    // Pause timer and save progress when closing
                    if (quizTimer) {
                      clearInterval(quizTimer);
                      setQuizTimer(null);
                    }
                    setIsPaused(true);
                    await saveProgress();
                    setShowQuizModal(false);
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className={styles.quizModalBody}>
              <div className={styles.questionCounter}>
                Question {currentQuestionIndex + 1} of {selectedQuiz.questions.length}
                {selectedQuiz.assignmentStatus === 'completed' || selectedQuiz.assignmentStatus === 'graded' ? (
                  <span className={styles.resultsMode}> - Results View</span>
                ) : null}
              </div>
              
              {selectedQuiz.questions[currentQuestionIndex] && (
                <div className={styles.questionCard}>
                  <h4 className={styles.questionText}>
                    {selectedQuiz.questions[currentQuestionIndex].question}
                  </h4>
                  
                  {selectedQuiz.questions[currentQuestionIndex].type === 'multiple-choice' && (
                    <div className={styles.optionsList}>
                      {selectedQuiz.questions[currentQuestionIndex].options.map((option, index) => {
                        const isSelected = quizResponses[selectedQuiz.questions[currentQuestionIndex]._id] === index;
                        const isCorrect = index === selectedQuiz.questions[currentQuestionIndex].correctAnswer;
                        const isViewingResults = selectedQuiz.assignmentStatus === 'completed' || selectedQuiz.assignmentStatus === 'graded';
                        
                        return (
                          <label key={index} className={`${styles.optionLabel} ${
                            isViewingResults ? styles.resultsOption : ''
                          } ${
                            isViewingResults && isCorrect ? styles.correctAnswer : ''
                          } ${
                            isViewingResults && isSelected && !isCorrect ? styles.incorrectAnswer : ''
                          }`}>
                            <input
                              type="radio"
                              name={`question_${selectedQuiz.questions[currentQuestionIndex]._id}`}
                              value={index}
                              checked={isSelected}
                              onChange={() => handleAnswerChange(
                                selectedQuiz.questions[currentQuestionIndex]._id, 
                                index
                              )}
                              disabled={isViewingResults}
                            />
                            <span className={styles.optionText}>{option}</span>
                            {isViewingResults && isCorrect && (
                              <span className={styles.correctIndicator}>✓ Correct Answer</span>
                            )}
                            {isViewingResults && isSelected && !isCorrect && (
                              <span className={styles.incorrectIndicator}>✗ Your Answer</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                  
                  {selectedQuiz.questions[currentQuestionIndex].type === 'true-false' && (
                    <div className={styles.optionsList}>
                      {selectedQuiz.questions[currentQuestionIndex].options.map((option, index) => {
                        const isSelected = quizResponses[selectedQuiz.questions[currentQuestionIndex]._id] === index;
                        const isCorrect = index === selectedQuiz.questions[currentQuestionIndex].correctAnswer;
                        const isViewingResults = selectedQuiz.assignmentStatus === 'completed' || selectedQuiz.assignmentStatus === 'graded';
                        
                        return (
                          <label key={index} className={`${styles.optionLabel} ${
                            isViewingResults ? styles.resultsOption : ''
                          } ${
                            isViewingResults && isCorrect ? styles.correctAnswer : ''
                          } ${
                            isViewingResults && isSelected && !isCorrect ? styles.incorrectAnswer : ''
                          }`}>
                            <input
                              type="radio"
                              name={`question_${selectedQuiz.questions[currentQuestionIndex]._id}`}
                              value={index}
                              checked={isSelected}
                              onChange={() => handleAnswerChange(
                                selectedQuiz.questions[currentQuestionIndex]._id, 
                                index
                              )}
                              disabled={isViewingResults}
                            />
                            <span className={styles.optionText}>{option}</span>
                            {isViewingResults && isCorrect && (
                              <span className={styles.correctIndicator}>✓ Correct Answer</span>
                            )}
                            {isViewingResults && isSelected && !isCorrect && (
                              <span className={styles.incorrectIndicator}>✗ Your Answer</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                  
                  {selectedQuiz.questions[currentQuestionIndex].questionType === 'fill-in-the-blank' && (
                    <div className={styles.fillInBlank}>
                      <input
                        type="text"
                        className={styles.textInput}
                        value={quizResponses[selectedQuiz.questions[currentQuestionIndex]._id] || ''}
                        onChange={(e) => handleAnswerChange(
                          selectedQuiz.questions[currentQuestionIndex]._id, 
                          e.target.value
                        )}
                        placeholder="Enter your answer..."
                      />
                    </div>
                  )}
                  
                  {selectedQuiz.questions[currentQuestionIndex].type === 'essay' && (
                    <div className={styles.essayInput}>
                      {(() => {
                        const isViewingResults = selectedQuiz.assignmentStatus === 'completed' || selectedQuiz.assignmentStatus === 'graded';
                        const studentAnswer = quizResponses[selectedQuiz.questions[currentQuestionIndex]._id];
                        
                        if (isViewingResults) {
                          return (
                            <div className={styles.essayResults}>
                              <div className={styles.essayAnswerDisplay}>
                                <strong style={{ color: 'var(--darkOrange)' }}>Your Answer:</strong>
                                <div className={styles.essayAnswerText}>
                                  {studentAnswer || 'No answer provided'}
                                </div>
                              </div>
                              <div className={styles.essayNote}>
                                <em>Note: Essay questions require manual grading by your teacher</em>
                              </div>
                            </div>
                          );
                        } else {
                          return (
                            <textarea
                              className={styles.textArea}
                              value={studentAnswer || ''}
                              onChange={(e) => handleAnswerChange(
                                selectedQuiz.questions[currentQuestionIndex]._id, 
                                e.target.value
                              )}
                              placeholder="Enter your essay response..."
                              rows={6}
                            />
                          );
                        }
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className={styles.quizModalFooter}>
              <button 
                className={styles.navButton}
                onClick={handlePreviousQuestion}
                disabled={currentQuestionIndex === 0}
              >
                Previous
              </button>
              
              <div className={styles.questionDots}>
                {selectedQuiz.questions.map((_, index) => (
                  <button
                    key={index}
                    className={`${styles.questionDot} ${
                      index === currentQuestionIndex ? styles.active : ''
                    } ${
                      quizResponses[selectedQuiz.questions[index]._id] !== undefined ? styles.answered : ''
                    }`}
                    onClick={async () => {
                      setCurrentQuestionIndex(index);
                      await saveProgress();
                    }}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
              
              {currentQuestionIndex === selectedQuiz.questions.length - 1 ? (
                (selectedQuiz.assignmentStatus === 'completed' || selectedQuiz.assignmentStatus === 'graded') ? (
                  <div className={styles.resultsSummary}>
                    <h4>Quiz Completed!</h4>
                    {(() => {
                      // Calculate current score including essay grades
                      let totalScore = 0;
                      let totalQuestions = selectedQuiz.questions.length;
                      let correctAnswers = 0;
                      let essayQuestions = 0;
                      let essayScore = 0;
                      
                      selectedQuiz.questions.forEach(question => {
                        const studentAnswer = quizResponses[question._id];
                        
                        if (question.type === 'essay') {
                          essayQuestions++;
                          // Check if essay has been graded
                          if (selectedQuiz.essayGrades && selectedQuiz.essayGrades[question._id]) {
                            essayScore += selectedQuiz.essayGrades[question._id].grade;
                            totalScore += selectedQuiz.essayGrades[question._id].grade;
                          } else {
                            // Essay not graded yet
                            totalScore += 0;
                          }
                        } else {
                          // Multiple choice, true/false, fill-in-the-blank
                          if (studentAnswer === question.correctAnswer) {
                            correctAnswers++;
                            totalScore += 100;
                          }
                        }
                      });
                      
                      const multipleChoiceScore = totalQuestions > essayQuestions ? 
                        Math.round((correctAnswers / (totalQuestions - essayQuestions)) * 100) : 0;
                      const finalScore = totalQuestions > 0 ? Math.round(totalScore / totalQuestions) : 0;
                      
                      return (
                        <div>
                          <p><strong>Final Score: {finalScore}%</strong></p>
                          {essayQuestions > 0 && (
                            <div className={styles.scoreBreakdown}>
                              <p>Multiple Choice: {multipleChoiceScore}%</p>
                              <p>Essay Questions: {essayQuestions > 0 ? 
                                (selectedQuiz.essayGrades && Object.keys(selectedQuiz.essayGrades).length > 0 ? 
                                  `${Math.round(essayScore / essayQuestions)}%` : 
                                  'Pending teacher grading') : 
                                'N/A'
                              }</p>
                              {essayQuestions > 0 && (!selectedQuiz.essayGrades || Object.keys(selectedQuiz.essayGrades).length === 0) && (
                                <p className={styles.pendingNote}>Essay questions are being graded by your teacher</p>
                              )}
                            </div>
                          )}
                          <p>You can review your answers above.</p>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <button 
                    className={styles.submitButton}
                    onClick={handleSubmitQuiz}
                  >
                    Submit Quiz
                  </button>
                )
              ) : (
                <button 
                  className={styles.navButton}
                  onClick={handleNextQuestion}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignedQuizzesTab;
